// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CircularProgress, Alert, TextField } from '@mui/material';
import { LockOutlined, CreditCard, OpenInNew, ContentCopy } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://agendali.com.br/api';

export default function PaymentWall() {
  const { user, logout } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [sub, setSub] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState<any>(null);
  const [copied, setCopied] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');

  const ps = user?.payment_status;
  const daysOverdue = ps?.days_overdue || 0;

  useEffect(() => {
    axios.get(`${API}/subscription`, { headers })
      .then(r => setSub(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRenew = async () => {
    setCheckoutLoading(true);
    try {
      if (!cpfCnpj || cpfCnpj.replace(/\D/g, '').length < 11) {
        alert('Informe o CPF ou CNPJ do responsável para continuar.');
        setCheckoutLoading(false);
        return;
      }
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan: sub?.plan || 'professional',
        name: user?.name,
        email: user?.email,
        cpf_cnpj: cpfCnpj,
      }, { headers });
      setCheckoutResult(res.data);
    } catch (e: any) {
      alert(e.response?.data?.message || 'Erro ao gerar cobrança');
    }
    setCheckoutLoading(false);
  };

  const formatCpfCnpj = (v: string) => {
    const n = v.replace(/\D/g, '').slice(0, 14);
    if (n.length <= 11) {
      return n
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
    }
    return n
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(''), 2000); });
  };

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2,
    }}>
      <Card sx={{ maxWidth: 480, width: '100%', borderRadius: 4, p: { xs: 3, md: 4 }, textAlign: 'center' }}>
        {/* Ícone */}
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#fff3e0', mx: 'auto', mb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LockOutlined sx={{ fontSize: 36, color: '#e65100' }} />
        </Box>

        <Typography fontWeight={900} fontSize={22} mb={0.5} color="#1a1a2e">
          Sistema temporariamente bloqueado
        </Typography>
        <Typography fontSize={14} color="text.secondary" mb={2.5}>
          Seu pagamento está em atraso há <strong style={{ color: '#d32f2f' }}>{daysOverdue} dias</strong>.
          Regularize para voltar a usar o Agendali.
        </Typography>

        {loading ? (
          <CircularProgress sx={{ my: 2 }} />
        ) : !checkoutResult ? (
          <>
            {sub?.plan && (
              <Alert severity="info" sx={{ mb: 2, textAlign: 'left', fontSize: 13 }}>
                Plano atual: <strong>{sub.plan}</strong> — R$ {sub.plan_price}/mês
              </Alert>
            )}
            <TextField
              fullWidth
              label="CPF ou CNPJ do responsável"
              value={cpfCnpj}
              onChange={e => setCpfCnpj(formatCpfCnpj(e.target.value))}
              inputProps={{ inputMode: 'numeric', maxLength: 18 }}
              size="small"
              sx={{ mb: 2, textAlign: 'left' }}
            />
            <Button fullWidth variant="contained" size="large" startIcon={<CreditCard />}
              onClick={handleRenew} disabled={checkoutLoading || cpfCnpj.replace(/\D/g, '').length < 11}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, borderRadius: 2.5, fontWeight: 800, py: 1.6, mb: 1.5 }}>
              {checkoutLoading ? <CircularProgress size={22} color="inherit" /> : 'Regularizar pagamento'}
            </Button>
            <Button variant="text" size="small" onClick={logout} sx={{ color: '#999', fontSize: 12 }}>
              Sair da conta
            </Button>
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
              Cobrança gerada! Realize o pagamento para desbloquear o sistema.
            </Alert>

            {checkoutResult.invoice_url && (
              <Box sx={{ mb: 2 }}>
                <Button fullWidth variant="contained" startIcon={<OpenInNew />}
                  onClick={() => window.open(checkoutResult.invoice_url, '_blank')}
                  sx={{ borderRadius: 2, fontWeight: 700, mb: 1, bgcolor: '#2563eb' }}>
                  Pagar agora
                </Button>
                <Button fullWidth variant="outlined" size="small"
                  startIcon={<ContentCopy />}
                  onClick={() => copy(checkoutResult.invoice_url, 'link')}
                  sx={{ borderRadius: 2, fontSize: 12 }}>
                  {copied === 'link' ? '✓ Copiado!' : 'Copiar link'}
                </Button>
              </Box>
            )}

            {checkoutResult.pix_key && (
              <Box sx={{ mb: 2 }}>
                <Typography fontWeight={700} fontSize={13} mb={0.5} textAlign="left">PIX Copia e Cola</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box sx={{ flex: 1, bgcolor: '#f5f5f5', borderRadius: 1.5, p: 1, fontSize: 10, fontFamily: 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {checkoutResult.pix_key}
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => copy(checkoutResult.pix_key, 'pix')}
                    sx={{ borderRadius: 1.5, minWidth: 70, fontSize: 11, flexShrink: 0 }}>
                    {copied === 'pix' ? '✓' : 'Copiar'}
                  </Button>
                </Box>
              </Box>
            )}

            <Typography fontSize={12} color="text.secondary">
              Após o pagamento ser confirmado, o sistema será liberado automaticamente.
            </Typography>
            <Button variant="text" size="small" onClick={logout} sx={{ color: '#999', fontSize: 12, mt: 1 }}>
              Sair da conta
            </Button>
          </>
        )}
      </Card>
    </Box>
  );
}
